import { Component } from '@theme/component';

/**
 * Designer grid component with category filtering.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} filtersContainer - The filter buttons container.
 * @property {HTMLButtonElement[]} filterButton - The category filter buttons.
 * @property {HTMLElement} listContainer - The designer cards grid.
 *
 * @extends Component<Refs>
 */
export class DesignerGrid extends Component {
  #items = [];

  connectedCallback() {
    super.connectedCallback();

    this.#items = [...(this.refs.listContainer?.querySelectorAll('.designer-grid__item') ?? [])];

    for (const btn of this.refs.filterButton ?? []) {
      btn.addEventListener('click', this.#handleFilterClick);
    }

    // Restore active filter from URL
    const params = new URLSearchParams(window.location.search);
    const category = params.get('category');
    if (category) {
      const button = Array.from(this.refs.filterButton ?? []).find((btn) => btn.dataset.category === category);
      if (button) {
        button.click();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const btn of this.refs.filterButton ?? []) {
      btn.removeEventListener('click', this.#handleFilterClick);
    }
  }

  /** @param {MouseEvent} event */
  #handleFilterClick = (event) => {
    const clickedButton = event.currentTarget;
    const category = clickedButton.dataset.category;
    const list = this.refs.listContainer;

    for (const btn of this.refs.filterButton ?? []) {
      btn.setAttribute('aria-pressed', 'false');
      btn.classList.remove('button-primary');
      btn.classList.add('button-secondary');
    }
    clickedButton.setAttribute('aria-pressed', 'true');
    clickedButton.classList.remove('button-secondary');
    clickedButton.classList.add('button-primary');

    // Sync active filter to URL
    const url = new URL(window.location);
    if (category) {
      url.searchParams.set('category', category);
    } else {
      url.searchParams.delete('category');
    }
    history.replaceState({}, '', url);

    if (!list) return;

    list.classList.remove('designer-grid__list--fade-in');
    list.classList.add('designer-grid__list--fade-out');

    list.addEventListener(
      'animationend',
      () => {
        for (const item of this.#items) {
          item.hidden = !(!category || item.dataset.category === category);
        }

        list.classList.remove('designer-grid__list--fade-out');
        list.classList.add('designer-grid__list--fade-in');
      },
      { once: true },
    );
  };
}

if (!customElements.get('designer-grid')) {
  customElements.define('designer-grid', DesignerGrid);
}
